// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/WorkTask.yaml
// Generated: 2026-02-24T18:44:19.438Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { workTaskService } from '@/services/taskmanagement/WorkTaskService';
import { CreateWorkTask, UpdateWorkTask, WorkTask } from '@/types/taskmanagement/WorkTask';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for WorkTask
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const worktaskKeys = {
  all: (tenantId?: string | null) => ['worktasks', tenantId] as const,
  lists: (tenantId?: string | null) => [...worktaskKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...worktaskKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...worktaskKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...worktaskKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all WorkTasks
 */
export function useWorkTasks(options?: Omit<UseQueryOptions<WorkTask[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<WorkTask[], Error>({
    queryKey: worktaskKeys.lists(tenantId),
    queryFn: () => workTaskService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single WorkTask by ID
 */
export function useWorkTask(
  id: string,
  options?: Omit<UseQueryOptions<WorkTask | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<WorkTask | undefined, Error>({
    queryKey: worktaskKeys.detail(id, tenantId),
    queryFn: () => workTaskService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new WorkTask
 */
export function useCreateWorkTask() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<WorkTask, Error, CreateWorkTask, { previousWorkTasks?: WorkTask[] }>({
    mutationFn: (data: CreateWorkTask) => workTaskService.create(data),
    onMutate: async (newWorkTask) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: worktaskKeys.lists(tenantId) });

      // Snapshot previous value
      const previousWorkTasks = queryClient.getQueryData<WorkTask[]>(worktaskKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<WorkTask[]>(worktaskKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newWorkTask, id: (newWorkTask as { id?: string }).id ?? 'temp-' + Date.now() } as WorkTask
      ]);

      return { previousWorkTasks };
    },
    onError: (_err, _newWorkTask, context) => {
      // Rollback on error
      if (context?.previousWorkTasks) {
        queryClient.setQueryData(worktaskKeys.lists(tenantId), context.previousWorkTasks);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: worktaskKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing WorkTask
 */
export function useUpdateWorkTask() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<WorkTask, Error, { id: string; data: UpdateWorkTask }, { previousWorkTask?: WorkTask; previousWorkTasks?: WorkTask[] }>({
    mutationFn: ({ id, data }) => workTaskService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: worktaskKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: worktaskKeys.lists(tenantId) });

      // Snapshot previous values
      const previousWorkTask = queryClient.getQueryData<WorkTask>(worktaskKeys.detail(id, tenantId));
      const previousWorkTasks = queryClient.getQueryData<WorkTask[]>(worktaskKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<WorkTask>(worktaskKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as WorkTask);

      // Optimistically update list
      queryClient.setQueryData<WorkTask[]>(worktaskKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as WorkTask : item))
      );

      return { previousWorkTask, previousWorkTasks };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousWorkTask) {
        queryClient.setQueryData(worktaskKeys.detail(id, tenantId), context.previousWorkTask);
      }
      if (context?.previousWorkTasks) {
        queryClient.setQueryData(worktaskKeys.lists(tenantId), context.previousWorkTasks);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: worktaskKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: worktaskKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a WorkTask
 */
export function useDeleteWorkTask() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousWorkTasks?: WorkTask[] }>({
    mutationFn: (id: string) => workTaskService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: worktaskKeys.lists(tenantId) });

      // Snapshot previous value
      const previousWorkTasks = queryClient.getQueryData<WorkTask[]>(worktaskKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<WorkTask[]>(worktaskKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousWorkTasks };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousWorkTasks) {
        queryClient.setQueryData(worktaskKeys.lists(tenantId), context.previousWorkTasks);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: worktaskKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveWorkTask() {
  const createMutation = useCreateWorkTask();
  const updateMutation = useUpdateWorkTask();

  return {
    mutate: (params: { id?: string; data: CreateWorkTask | UpdateWorkTask }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateWorkTask }, options);
      } else {
        return createMutation.mutate(params.data as CreateWorkTask, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
